import type { Practitioner } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { asClinicalTaskStatus } from '@eluve/clinical-tasks';
import { formatHumanName } from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';
import {
  type ClinicalTaskEdit,
  type PractitionerOption,
  applyEditToTask,
  commitTaskWrite,
  toWriteInput,
} from '@eluve/mini-apps';

import type { ClinicalTaskRow } from './fetch-clinical-task-rows';

/**
 * Write actions for the tenant Tasks detail sheet, per row — the same split as
 * the appointment panel, keyed by each row's own appointmentId:
 *
 * - a **draft** edit rides the ts-rest update route (stays pending; the backend
 *   folds a chosen status and writes reviewer attestation);
 * - an **accepted** edit writes browser-direct under the clinician's own
 *   Medplum identity (the update route is pending-only), bundling an
 *   attestation Provenance on a status change — the same `commitTaskWrite` the
 *   panel uses, so the audit trail is identical whichever surface edits;
 * - approve / reject rides the ts-rest review route.
 *
 * Loads the tenant's Practitioner list once for the assignee select.
 */
export function useClinicalTaskMutations(tenantId: string) {
  const apiClient = useApiClient();
  const { client, practitionerId } = useMedplum();
  // The acting clinician, for the attestation Provenance a lifecycle change
  // records. Null until the Medplum session resolves — a write still succeeds,
  // just without an attributable Provenance (best-effort audit, as the panel).
  const practitionerReference = practitionerId
    ? `Practitioner/${practitionerId}`
    : null;
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);

  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    void (async () => {
      try {
        const found = await client.searchResources('Practitioner', {
          _count: 200,
        });
        if (cancelled) return;
        setPractitioners(
          found
            .filter((p): p is Practitioner & { id: string } => Boolean(p.id))
            .map((p) => ({
              reference: `Practitioner/${p.id}`,
              // Same name resolution the "Assigned to" column uses; the id
              // fallback covers a (rare) nameless practitioner.
              display: formatHumanName(p.name?.[0]) || `Practitioner/${p.id}`,
            }))
            .sort((a, b) => a.display.localeCompare(b.display)),
        );
      } catch {
        if (!cancelled) setPractitioners([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  /** Persist a field edit. Draft → ts-rest update (stays pending / folds a
   *  chosen status); accepted → browser-direct commit with attestation. */
  const saveEdit = useCallback(
    async (row: ClinicalTaskRow, edit: ClinicalTaskEdit): Promise<boolean> => {
      try {
        if (row.status === 'draft') {
          // A draft edit rides the appointment-scoped ts-rest route — only an
          // appointment-derived task is ever a pending draft.
          if (!row.appointmentId) return false;
          const res = await apiClient.clinicalTasks.updateTask({
            params: { tenantId, taskId: row.id },
            body: {
              appointmentId: row.appointmentId,
              ...toWriteInput(edit),
              // The status the clinician picked; the route folds it into intent
              // + review tag and attests when it's a decided value. Unknown/
              // absent falls back to draft (keeps the task pending).
              status: asClinicalTaskStatus(edit.status) ?? 'draft',
            },
          });
          return res.status === 200;
        }
        // Accepted edit writes browser-direct under the clinician's own
        // identity — no appointment needed, so standalone (Tasks-view) tasks
        // created with no encounter are editable too.
        if (!client) return false;
        const next = applyEditToTask(row.raw, edit);
        // A status change is a lifecycle decision → attestation Provenance; a
        // pure field edit is not (matches the backend's non-status edit).
        await commitTaskWrite(client, next, {
          attest: next.status !== row.raw.status,
          practitionerReference,
        });
        client.invalidateSearches('Task');
        return true;
      } catch {
        return false;
      }
    },
    [apiClient, tenantId, client, practitionerReference],
  );

  /** Create a standalone task from the Tasks view — no encounter, scoped to a
   *  directly-chosen tenant patient. Born accepted (To do), like the
   *  appointment-panel create, but the server derives the FHIR Patient from
   *  `patientId` instead of an appointment. */
  const createStandaloneTask = useCallback(
    async (patientId: string, edit: ClinicalTaskEdit): Promise<boolean> => {
      try {
        const res = await apiClient.clinicalTasks.createTask({
          params: { tenantId },
          body: { patientId, ...toWriteInput(edit) },
        });
        if (res.status !== 201) return false;
        client?.invalidateSearches('Task');
        return true;
      } catch {
        return false;
      }
    },
    [apiClient, tenantId, client],
  );

  /** Draft-only review decision via the ts-rest review route. */
  const decide = useCallback(
    async (
      row: ClinicalTaskRow,
      decision: 'approve' | 'reject',
    ): Promise<boolean> => {
      if (!row.appointmentId) return false;
      try {
        const res = await apiClient.clinicalTasks.reviewTask({
          params: { tenantId, taskId: row.id },
          body: { appointmentId: row.appointmentId, decision },
        });
        if (res.status !== 200) return false;
        client?.invalidateSearches('Task');
        return true;
      } catch {
        return false;
      }
    },
    [apiClient, tenantId, client],
  );

  return useMemo(
    () => ({ practitioners, saveEdit, decide, createStandaloneTask }),
    [practitioners, saveEdit, decide, createStandaloneTask],
  );
}
